import { HttpClient } from '@adapters/HttpClient';
import { Order } from '../models/Orders';

// Mapeo de columnas del frontend (AgGrid field) a columnas del backend (export API)
const COLUMN_MAPPING: Record<string, string> = {
  ref: 'ref',
  shop: 'shop',
  status: 'tracking_stage',
  courier: 'courier',
  address: 'address',
};

// Columnas por defecto para exportación (todas las disponibles)
const DEFAULT_EXPORT_COLUMNS = [
  'id',
  'ref',
  'shop',
  'customer_name',
  'status',
  'tracking_stage',
  'total',
  'payment_method',
  'creation_date',
  'processed_at',
  'client_country',
  'address',
  'shipping_phone',
  'date_tracking_update',
  'external_financial_status',
  'courier',
  'tracking_number',
];

// Función para mapear columnas del frontend al backend
function mapVisibleColumnsToExport(visibleColumns: string[]): string[] {
  if (!visibleColumns || visibleColumns.length === 0) {
    return DEFAULT_EXPORT_COLUMNS;
  }

  const mappedColumns = new Set<string>();
  // Siempre incluir 'id' como primera columna
  mappedColumns.add('id');

  visibleColumns.forEach((frontendCol) => {
    const backendCol = COLUMN_MAPPING[frontendCol];
    if (backendCol) {
      mappedColumns.add(backendCol);
    }
  });

  // Si no se mapeó ninguna columna válida, usar las por defecto
  if (mappedColumns.size <= 1) {
    return DEFAULT_EXPORT_COLUMNS;
  }

  return Array.from(mappedColumns);
}

export interface OrdersFilters {
  searchFilter?: { filters: any[]; logic: string };
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
  page?: number;
  limit?: number;
  status_ids?: number;
  shipment_status_ids?: number;
}

export interface ExportOrdersPayload {
  advanced_filters?: {
    filters: Array<{
      column: string;
      operator: string;
      value?: any;
    }>;
    logic: string;
  };
  sort_by?: string;
  sort_direction?: 'asc' | 'desc';
  status_ids?: number | string;
  shipment_status_ids?: number;
}

export interface ExportOrdersResponse {
  url_file: string;
}

export interface ExportOrdersBody {
  columns: string[];
}

export interface OrderDetailsFilters {
  ids: number[];
  page?: number;
  per_page?: number;
}

export interface OrdersPage {
  orders: Order[];
  nextPage?: number;
  hasMore: boolean;
  total: number;
}

function buildAdvancedFiltersParams(searchFilter: {
  filters: any[];
  logic: string;
}) {
  const params: Record<string, any> = {};
  if (searchFilter.filters && Array.isArray(searchFilter.filters)) {
    searchFilter.filters.forEach((f, i) => {
      params[`advanced_filters[filters][${i}][column]`] = f.column;
      params[`advanced_filters[filters][${i}][operator]`] = f.operator;
      // Solo enviar value si existe (para operadores is_null / is_not_null no debe enviarse)
      if (
        f.value !== undefined &&
        f.value !== null &&
        !(Array.isArray(f.value) && f.value.length === 0)
      ) {
        params[`advanced_filters[filters][${i}][value]`] = f.value;
      }
    });
  }
  if (searchFilter.logic) {
    params['advanced_filters[logic]'] = searchFilter.logic;
  }
  return params;
}

// 🎯 Construye los query params para exportación como string
function buildExportQueryString(payload: ExportOrdersPayload): string {
  const params: Record<string, any> = {};

  // Advanced filters aplanados
  if (payload.advanced_filters) {
    const af = buildAdvancedFiltersParams(payload.advanced_filters as any);
    Object.assign(params, af);
  }

  // Sorting (defaults idénticos al GET para shipped)
  const sortBy = payload.sort_by || 'order_created_at';
  const sortDirection = payload.sort_direction || 'desc';
  params['sorting[column]'] = sortBy;
  params['sorting[direction]'] = sortDirection;

  // status_ids: permitir string ("6,7,8") o number; default shipped
  const statusIds =
    payload.status_ids !== undefined && payload.status_ids !== null
      ? payload.status_ids
      : '6,7,8';
  params.status_ids = statusIds;

  // shipment_status_ids opcional
  if (
    payload.shipment_status_ids !== undefined &&
    payload.shipment_status_ids !== null
  ) {
    params.shipment_status_ids = payload.shipment_status_ids;
  }

  // Construir query string
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      searchParams.append(key, String(value));
    }
  });

  return searchParams.toString();
}

export class OrdersApiAdapter {
  constructor(private httpClient: HttpClient) { }

  async getOrdersPaginated(filters: OrdersFilters = {}): Promise<OrdersPage> {
    const {
      searchFilter,
      sortBy = 'order_created_at',
      sortDirection = 'desc',
      page = 1,
      limit = 20,
      status_ids = '6,7,8',
      shipment_status_ids,
    } = filters;

    const params: any = {
      ...(sortBy && { 'sorting[column]': sortBy }),
      ...(sortDirection && { 'sorting[direction]': sortDirection }),
      page,
      per_page: limit,
      status_ids,
      ...(shipment_status_ids && { shipment_status_ids }),
      ...(searchFilter ? buildAdvancedFiltersParams(searchFilter) : {}),
    };

    // Solo mostrar loading global para la primera página
    const showGlobalLoading = page === 1;
    const response = await this.httpClient.get<any>(
      '/orders',
      params,
      {},
      showGlobalLoading
    );

    return {
      orders: response.data,
      nextPage:
        response.meta.currentPage < response.meta.lastPage
          ? response.meta.currentPage + 1
          : undefined,
      hasMore: response.meta.currentPage < response.meta.lastPage,
      total: response.meta.total,
    };
  }

  async getAll(): Promise<Order[]> {
    const response = await this.getOrdersPaginated();
    return response.orders;
  }

  async getOrderById(id: number): Promise<Order> {
    const response = await this.httpClient.get<Order>(`/orders/${id}`);
    return response;
  }

  async getOrdersByIdsPaginated(
    filters: OrderDetailsFilters = {}
  ): Promise<OrdersPage> {
    const { ids, page = 1, per_page = 100 } = filters;

    const params: any = {
      page,
      per_page,
      ids: ids.join(','),
    };

    // Solo mostrar loading global para la primera página
    const showGlobalLoading = page === 1;
    const response = await this.httpClient.get<any>(
      '/orders',
      params,
      {},
      showGlobalLoading
    );

    return {
      orders: response.data,
      nextPage:
        response.meta.currentPage < response.meta.lastPage
          ? response.meta.currentPage + 1
          : undefined,
      hasMore: response.meta.currentPage < response.meta.lastPage,
      total: response.meta.total,
    };
  }

  async cancelOrder(id: number): Promise<void> {
    await this.httpClient.put(`/orders/${id}/cancel`, {});
  }

  async uncancelOrder(id: number): Promise<void> {
    await this.httpClient.put(`/orders/${id}/uncancel`, {});
  }

  async markAsPreparation(
    ids: number[],
    preparationType: number = 1
  ): Promise<void> {
    await this.httpClient.put('/orders/mark-as-preparation', {
      ids,
      preparation_type: preparationType,
    });
  }

  async confirmOrder(id: number): Promise<void> {
    await this.httpClient.put(`/orders/${id}/confirm`, {});
  }

  async confirmOrders(ids: number[]): Promise<void> {
    // Si es un solo ID, usar el endpoint individual
    if (ids.length === 1) {
      await this.confirmOrder(ids[0]);
      return;
    }

    // Para múltiples IDs, usar el endpoint de batch
    await this.httpClient.put('/orders/confirm', {
      ids,
    });
  }

  async updateFromStore(id: number): Promise<void> {
    await this.httpClient.put(`/orders/${id}/update-from-store`, {});
  }

  async unassignStock(id: number): Promise<void> {
    await this.httpClient.put(`/orders/${id}/unassign-stock`, {});
  }

  async exportOrders(
    payload: ExportOrdersPayload,
    visibleColumns?: string[]
  ): Promise<ExportOrdersResponse> {
    const queryString = buildExportQueryString(payload);
    const url = `/orders/export${queryString ? `?${queryString}` : ''}`;
    const columns = mapVisibleColumnsToExport(visibleColumns || []);
    const body: ExportOrdersBody = { columns };
    return await this.httpClient.post(url, body);
  }
}
